import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Button, Paper, IconButton, Modal, TextField, Avatar,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import editIcon from '../../assets/edit-icon.svg';
import AlertTriangle from '../../assets/AlertTriangle.svg';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { getCommandCentres, setCommandCentreActive } from '../../services/commandCentreService';
import { pageBoxSx, headerRowSx, leftTextSx, nodeDetailsSx, addAccountSX, allSelectCombinedSx, controlsRowSx } from './commandCentre.styles';
import { modalContainerSx, modalHeaderSx, modalTitleSx, modalCloseIconSx, modalContentSx, modalWarningRowSx, modalCancelButtonSx, modalDeactivateButtonSx, modalBodyTextSx } from '../node/createNode.styles';
import { nodeCitySx, activeTagSx, inactiveTagSx, activeDotColor, inactiveDotColor } from '../node/operatingNodes.styles';
import CreateCommandCentreDrawer from './CreateCommandCentreDrawer';

type CommandCentreRow = {
  id: number;
  name: string;
  city: string;
  state: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  radius: number | null;
  staffCount: number | null;
  status: 'Active' | 'Inactive';
};

interface Props {
  onEdit?: (cc: any) => void;
  notification?: { show: boolean; message?: string; severity?: 'success' | 'error' };
}

const CommandCentre: React.FC<Props> = ({ onEdit, notification }) => {
  const [rowsData, setRowsData] = useState<CommandCentreRow[]>([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const gridContainerRef = useRef<HTMLDivElement | null>(null);

  // Create / edit drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerInitial, setDrawerInitial] = useState<any>(null);

  // Confirm deactivate modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<CommandCentreRow | null>(null);

  const load = React.useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      const apiPage = (paginationModel.page ?? 0) + 1;
      const resp = await getCommandCentres(apiPage, paginationModel.pageSize, signal);
      const centres = resp.command_centres;
      let normalized: CommandCentreRow[] = centres.map((c: any, idx: number) => ({
        id: c.id ?? c.command_centre_id ?? idx,
        name: c.name ?? '',
        city: c.city ?? '',
        state: c.state ?? '',
        address: c.address ?? '',
        latitude: c.latitude ?? null,
        longitude: c.longitude ?? null,
        radius: c.radius ?? null,
        staffCount: c.staff_count ?? c.staffCount ?? null,
        status: (c.active === false || c.status === 'Inactive') ? 'Inactive' : 'Active',
      }));
      if (statusFilter === 'Active') normalized = normalized.filter((r) => r.status === 'Active');
      else if (statusFilter === 'Inactive') normalized = normalized.filter((r) => r.status === 'Inactive');
      setRowsData(normalized);
      const total = resp.meta?.total ?? resp.meta?.count ?? normalized.length;
      setRowCount(Number(total) || normalized.length);
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: err?.message ?? 'Failed to load command centres' } }));
    } finally {
      setLoading(false);
    }
  }, [paginationModel, statusFilter]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const openCreate = () => { setDrawerInitial(null); setDrawerOpen(true); };
  const openConfirm = (row: CommandCentreRow) => { setConfirmTarget(row); setConfirmOpen(true); };
  const closeConfirm = () => { setConfirmOpen(false); setConfirmTarget(null); };

  const confirmToggle = async () => {
    if (!confirmTarget) return;
    // KAN-129: derive action from current status — same flow activates or deactivates.
    const nextActive = confirmTarget.status === 'Inactive';
    try {
      await setCommandCentreActive(confirmTarget.id, nextActive);
      setRowsData((r) => r.map((x) => x.id === confirmTarget.id ? { ...x, status: nextActive ? 'Active' : 'Inactive' } : x));
      window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: nextActive ? 'Command Centre activated' : 'Command Centre deactivated' } }));
    } catch (err: any) {
      window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: err?.message ?? (nextActive ? 'Failed to activate' : 'Failed to deactivate') } }));
    }
    closeConfirm();
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Command Centre',
      flex: 1.4,
      minWidth: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const initials = (params.row.name as string)
          .split(' ')
          .filter(Boolean)
          .slice(0, 2)
          .map((w: string) => w[0].toUpperCase())
          .join('');
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 32, height: 32, fontSize: '11px', fontWeight: 700, fontFamily: 'Inter, Arial, sans-serif', bgcolor: '#143133', flexShrink: 0 }}>
              {initials}
            </Avatar>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
              <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, fontSize: '13px', lineHeight: '20px', color: '#111827' }}>
                {params.row.name}
              </Typography>
              {params.row.address && (
                <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 400, fontSize: '11px', lineHeight: '16.5px', color: '#9CA3AF' }}>
                  {params.row.address}
                </Typography>
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1,
      minWidth: 160,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const { city, state } = params.row;
        if (!city && !state) return <Typography sx={nodeCitySx as any}>—</Typography>;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {city && <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '19.5px', color: '#111827' }}>{city}</Typography>}
            {state && <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 400, fontSize: '11px', lineHeight: '16.5px', color: '#9CA3AF' }}>{state}</Typography>}
          </Box>
        );
      },
    },
    {
      field: 'radius',
      headerName: 'Radius',
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const r = params.row.radius;
        const isInactive = params.row.status === 'Inactive';
        const label = isInactive || r == null ? '—' : `${parseFloat((r / 1000).toFixed(2))} km`;
        return <Typography sx={nodeCitySx as any}>{label}</Typography>;
      },
    },
    {
      field: 'staffCount',
      headerName: 'Staff',
      flex: 0.5,
      minWidth: 80,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const isInactive = params.row.status === 'Inactive';
        const count = params.row.staffCount;
        return <Typography sx={nodeCitySx as any}>{isInactive ? 'No Staff' : (count == null ? '—' : count)}</Typography>;
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.6,
      minWidth: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) =>
        params.value === 'Active' ? (
          <Box component="span" sx={activeTagSx}>
            <Box sx={{ position: 'absolute', width: 6, height: 6, borderRadius: '9999px', bgcolor: activeDotColor, top: '9.25px', left: '10px' }} />
            <Box component="span" sx={{ pl: '18px' }}>{params.value}</Box>
          </Box>
        ) : (
          <Box component="span" sx={inactiveTagSx}>
            <Box sx={{ position: 'absolute', width: 6, height: 6, borderRadius: '9999px', bgcolor: inactiveDotColor, top: '9.25px', left: '10px' }} />
            <Box component="span" sx={{ pl: '18px' }}>{params.value}</Box>
          </Box>
        ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      minWidth: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const isInactive = params.row.status === 'Inactive';
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              disabled={isInactive}
              onClick={() => !isInactive && onEdit && onEdit(params.row)}
            >
              <Box component="img" src={editIcon} alt="Edit" sx={{ width: 14, height: 14, opacity: isInactive ? 0.4 : 1 }} />
            </IconButton>
            {/* KAN-129: power toggle stays enabled for inactive rows to allow reactivation. */}
            <IconButton
              size="small"
              title={isInactive ? 'Activate' : 'Deactivate'}
              onClick={() => openConfirm(params.row)}
            >
              <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <PowerSettingsNewIcon sx={{ color: isInactive ? '#9CA3AF' : '#6B7280', fontSize: '16px' }} />
                {isInactive && (
                  <Box sx={{ position: 'absolute', width: '22px', height: '2px', bgcolor: '#9CA3AF', transform: 'rotate(45deg)' }} />
                )}
              </Box>
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={pageBoxSx}>
      <Box mb={2}>
        <Box sx={{ ...headerRowSx, position: 'relative' }}>
          <Box sx={leftTextSx}>
            <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 700, fontSize: '20px', lineHeight: '30px' }}>
              Command Centres
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 400, fontSize: '13px', lineHeight: '19.5px', color: '#6B7280' }}>
              Manage your command centre locations and staff.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button variant="outlined" sx={addAccountSX} onClick={openCreate}>
              + Add Command Centre
            </Button>
            {notification?.show && (
              <Box sx={{ position: 'absolute', bottom: 'calc(0%)', right: 0, width: '356px', height: '54px', borderRadius: '8px', backgroundColor: notification.severity === 'error' ? '#FEF2F2' : '#ECFDF3', border: `1px solid ${notification.severity === 'error' ? '#FCA5A5' : '#A9DEBF'}`, display: 'flex', alignItems: 'center', px: 2, zIndex: 1200 }}>
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: notification.severity === 'error' ? '#B91C1C' : '#008A2E', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1 }}>
                  {notification.severity === 'error'
                    ? <svg width="13" height="13" viewBox="0 0 8 8" fill="none"><path d="M1 1L7 7M7 1L1 7" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" /></svg>
                    : <svg width="13" height="13" viewBox="0 0 8 6" fill="none"><path d="M1 3L3 5L7 1" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  }
                </Box>
                <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px' }}>{notification.message}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Status filter */}
      <Box sx={{ ...controlsRowSx, mb: 2 }}>
        <Autocomplete
          size="small"
          sx={{ ...allSelectCombinedSx, minWidth: 160 }}
          options={['Active', 'Inactive']}
          value={statusFilter}
          onChange={(_e, val) => setStatusFilter(val)}
          renderInput={(params) => <TextField {...params} placeholder="All Statuses" />}
          clearOnEscape
        />
      </Box>

      <Paper sx={nodeDetailsSx}>
        <div ref={gridContainerRef} style={{ height: '100%', width: '100%' }}>
          <DataGrid
            loading={loading}
            rows={rowsData}
            columns={columns}
            columnHeaderHeight={40}
            rowHeight={56}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={rowCount}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            getRowClassName={(params) => params.row.status === 'Inactive' ? 'row-inactive' : ''}
            sx={{
              '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F9FAFB !important', borderBottom: '1px solid rgba(0,0,0,0.06)', minHeight: '40px !important', maxHeight: '40px !important', position: 'sticky', top: 0, zIndex: 2 },
              '& .MuiDataGrid-columnHeader': { backgroundColor: '#F9FAFB !important', minHeight: '40px !important', maxHeight: '40px !important' },
              '& .MuiDataGrid-columnHeaderTitle': { fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, fontSize: '11px', lineHeight: '16.5px', letterSpacing: '0.06px' },
              '& .MuiDataGrid-columnSeparator': { display: 'none' },
              '& .MuiDataGrid-cell': { borderRight: 'none', display: 'flex', alignItems: 'center' },
              '& .MuiDataGrid-virtualScroller': { maxHeight: 'calc(100vh - 260px)', overflow: 'auto', paddingBottom: '56px' },
              '& .MuiDataGrid-footerContainer': { position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 4, background: '#FFFFFF', borderTop: '1px solid rgba(0,0,0,0.06)' },
              '& .row-inactive': { opacity: 0.45 },
            }}
          />
        </div>
      </Paper>

      {/* Create / Edit Drawer */}
      <CreateCommandCentreDrawer
        open={drawerOpen}
        initial={drawerInitial}
        onClose={() => setDrawerOpen(false)}
        onSaved={(msg) => {
          setDrawerOpen(false);
          load();
          if (msg) window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: msg } }));
        }}
      />

      {/* Confirm Activate/Deactivate Modal — KAN-129 */}
      <Modal open={confirmOpen} onClose={closeConfirm}>
        <Box sx={modalContainerSx}>
          <Box sx={modalHeaderSx}>
            <Typography sx={modalTitleSx}>{confirmTarget?.status === 'Inactive' ? 'Activate Command Centre?' : 'Deactivate Command Centre?'}</Typography>
            <IconButton size="small" onClick={closeConfirm} sx={modalCloseIconSx}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </IconButton>
          </Box>
          <Box sx={modalContentSx}>
            <Box sx={modalWarningRowSx}>
              <Box sx={{ width: '80px', height: '36px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', backgroundColor: '#FEF2F2' }}>
                <Box component="img" src={AlertTriangle} sx={{ width: '16px', height: '16px', objectFit: 'contain' }} />
              </Box>
              <Typography sx={modalBodyTextSx}>
                {confirmTarget?.status === 'Inactive'
                  ? `Activating "${confirmTarget?.name}" will re-enable this command centre.`
                  : `Deactivating "${confirmTarget?.name}" will disable this command centre.`}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button sx={modalCancelButtonSx} onClick={closeConfirm}>Cancel</Button>
              <Button sx={modalDeactivateButtonSx} onClick={confirmToggle}>{confirmTarget?.status === 'Inactive' ? 'Activate' : 'Deactivate'}</Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default CommandCentre;
